export enum WidgetType {
    BK_WIDGET_3 = 'bk_w_3'
}

export enum CheckoutInitiatorLocation {
    FLOATING_WIDGET_LIST,
    FLOATING_WIDGET_DATE,
    BOOK_NOW_WIDGET_DATE,
}

export interface CheckoutInitiatorProps {
    type: WidgetType;
    id: string;
    location?: CheckoutInitiatorLocation;
}

export class CheckoutInitiator {
    public type: WidgetType;
    public id: string;
    public location?: CheckoutInitiatorLocation;
    static readonly urlParam = 'ci';
    // toUrlParameter() joins the parts with '_', so an id with '_' is read wrongly by the backend.
    static readonly urlPartSeparator = '_';

    static isValidId(id: string): boolean {
        return !id.includes(CheckoutInitiator.urlPartSeparator);
    }

    constructor({ type, id, location }: CheckoutInitiatorProps) {
        if (type === undefined || id === undefined ) {
            throw new Error('Missing required properties');
        }

        if (!CheckoutInitiator.isValidId(id)) {
            throw new Error(`Invalid id: must not contain '${CheckoutInitiator.urlPartSeparator}'`);
        }

        if (!Object.values(WidgetType).includes(type)) {
            throw new Error('Invalid type');
        }

        if (location && !Object.values(CheckoutInitiatorLocation).includes(location)) {
            throw new Error('Invalid location');
        }

        this.type = type;
        this.id = id;
        if (typeof location !== 'undefined') {
            this.location = location;
        }
    }

    static floatingWidgetList(id: string): CheckoutInitiator {
        return new CheckoutInitiator({
            type: WidgetType.BK_WIDGET_3,
            id,
            location: CheckoutInitiatorLocation.FLOATING_WIDGET_LIST,
        });
    }

    static floatingWidgetDate(id: string): CheckoutInitiator {
        return new CheckoutInitiator({
            type: WidgetType.BK_WIDGET_3,
            id,
            location: CheckoutInitiatorLocation.FLOATING_WIDGET_DATE,
        });
    }

    static bookNowWidgetDate(id: string): CheckoutInitiator {
        return new CheckoutInitiator({
            type: WidgetType.BK_WIDGET_3,
            id,
            location: CheckoutInitiatorLocation.BOOK_NOW_WIDGET_DATE,
        });
    }

    static unknownLocation(id: string): CheckoutInitiator {
        return new CheckoutInitiator({
            type: WidgetType.BK_WIDGET_3,
            id,
        });
    }

    public toUrlParameter(): string {
        const typeCodeMap = {
            [WidgetType.BK_WIDGET_3]: 'w3',
        };

        const locationCodeMap = {
            [CheckoutInitiatorLocation.FLOATING_WIDGET_LIST]: 'fl',
            [CheckoutInitiatorLocation.FLOATING_WIDGET_DATE]: 'fd',
            [CheckoutInitiatorLocation.BOOK_NOW_WIDGET_DATE]: 'xd',
        };

        const typeCode = typeCodeMap[this.type];
        if (!typeCode) {
            throw new Error('Unsupported CheckoutInitiator encoding: unknown type');
        }
        const parts = [typeCode, this.id];

        if (typeof this.location !== 'undefined') {
            const locationCode = locationCodeMap[this.location];
            if (!locationCode) {
                throw new Error('Unsupported CheckoutInitiator encoding: unknown location');
            }
            parts.push(locationCode);
        }

        return parts.join(CheckoutInitiator.urlPartSeparator);
    }
}
